import { LoadState } from "../../../constants/enums";

/**
 * A single drift record between the L1 snapshot (gl_monthly_summary) and the
 * L0 raw ledger (gl_invoice_entry) for a given GL account × branch × fiscal month.
 * Requirement 5.3
 */
export interface IReconcileDifference {
  fiscal_year_month_code: string;
  gl_account_code: string;
  branch_code: string;
  snapshot_debit: number;
  snapshot_credit: number;
  snapshot_entry_count: number;
  raw_debit: number;
  raw_credit: number;
  raw_entry_count: number;
}

/**
 * Response of GET /accounting/gl-monthly-summary/reconcile.
 * `clean === true` means every snapshot reconciles with the ledger.
 * Requirement 5.1, 5.2, 5.3
 */
export interface IReconcileResponse {
  differences: IReconcileDifference[];
  clean: boolean;
}

/**
 * Response of POST /accounting/gl-monthly-summary/rebuild.
 * One entry per rebuilt fiscal month; `ok` flags per-month success.
 * Requirement 5.4, 5.5
 */
export interface IRebuildResponse {
  months: Array<{
    fiscal_year_month_code: string;
    ok: boolean;
    summary?: unknown;
    error?: string;
  }>;
}

/**
 * Response of GET /accounting/fiscal-calendar/validate.
 * Requirement 12.2
 */
export interface ICalendarValidationResponse {
  periods_checked: number;
  gaps: unknown[];
  overlaps: unknown[];
  uncovered_entries: unknown[];
  ok: boolean;
}

/**
 * Response of POST /accounting/gl-fiscal-year-close/detect-historic.
 * Requirement 12.2
 */
export interface IHistoricDetectionResponse {
  retained_earnings_gl: string;
  tolerance_pct: number;
  tolerance_min: number;
  dry_run: boolean;
  candidates_checked: number;
  flagged: unknown[];
  report: unknown;
}

/**
 * Response of POST /accounting/gl-monthly-summary/backfill-runbook.
 * Requirement 12.2, 12.4
 */
export interface IBackfillRunbookResponse {
  started_at: string;
  steps: {
    calendar: unknown;
    historic: unknown;
    backfill: unknown;
    reconcile: unknown;
  };
  aborted: boolean;
  aborted_at?: string;
  ok: boolean;
}

/**
 * Redux slice state for the GL Summary Reconcile + Admin Runbook screens.
 * Holds the reconcile payload plus per-operation LoadState fields.
 * Requirement 14.1, 14.3, 14.5
 */
export interface IGLSummaryReconcileState {
  reconcile: IReconcileResponse | null;
  reconcileLoading: LoadState;
  rebuildLoading: LoadState;
  calendarValidationLoading: LoadState;
  historicDetectionLoading: LoadState;
  backfillRunbookLoading: LoadState;
  nightlyReconcileLoading: LoadState;
  error: string | null;
}

export const initialGLSummaryReconcileState: IGLSummaryReconcileState = {
  reconcile: null,
  reconcileLoading: LoadState.NotLoaded,
  rebuildLoading: LoadState.NotLoaded,
  calendarValidationLoading: LoadState.NotLoaded,
  historicDetectionLoading: LoadState.NotLoaded,
  backfillRunbookLoading: LoadState.NotLoaded,
  nightlyReconcileLoading: LoadState.NotLoaded,
  error: null,
};
